import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { QcDialog } from "@/components/stores/StoreDialogs";
import { qty, unit } from "@/components/stores/storesUi";

const daysSince = (d) => (d ? Math.max(0, Math.floor((Date.now() - new Date(`${String(d).slice(0, 10)}T00:00:00`).getTime()) / 86_400_000)) : null);

/** Received rolls waiting for incoming QC: stores cannot issue them until QC records the AR number. One line per
 *  receipt and item, oldest first; approve or reject all of its rolls, or open it and pick rolls. */
export default function QcMaterials() {
  const [rolls, setRolls] = useState(null);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState({});
  const [picked, setPicked] = useState({});
  const [qcFor, setQcFor] = useState(null);
  const [q, setQ] = useState("");
  const seq = useRef(0);

  const load = () => {
    const id = ++seq.current;
    return api.get("/stores/rolls", { params: { qc_status: "PENDING", available: true, limit: 500, q: q.trim() || undefined } })
      .then(({ data }) => { if (id === seq.current) { setRolls(data.items); setTotal(data.total); } })
      .catch(() => setRolls((r) => r || []));
  };
  useEffect(() => { const t = setTimeout(load, q ? 250 : 0); return () => clearTimeout(t); }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  useLiveRefresh("stores", load);

  const groups = Object.values((rolls || []).reduce((acc, r) => {
    const key = [r.received_date, r.supplier, r.grn_no, r.item_code].join("|");
    (acc[key] ||= { key, received: r.received_date, supplier: r.supplier, grn: r.grn_no, item: r.item_name, code: r.item_code,
      width: r.width, uom: r.uom, rolls: [] }).rolls.push(r);
    return acc;
  }, {})).sort((a, b) => String(a.received).localeCompare(String(b.received)) || String(a.code).localeCompare(String(b.code)));

  const done = () => { setQcFor(null); setPicked({}); load(); };
  const chosen = (g) => g.rolls.filter((r) => picked[r.roll_no]).map((r) => r.roll_no);

  return (
    <section className="border border-neutral-200 bg-white" data-testid="widget-qc-materials">
      <header className="px-5 py-3 border-b border-neutral-200 flex flex-wrap items-baseline justify-between gap-2">
        <div className="label-caps">Materials waiting for QC{rolls ? ` · ${total.toLocaleString("en-IN")} roll${total === 1 ? "" : "s"}` : ""}</div>
        <div className="text-xs text-neutral-500">Stores can issue a received roll only after QC approves it. Record the AR number to release it.</div>
      </header>
      <div className="px-5 py-2.5 border-b border-neutral-100">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search supplier, GRN, material, item code, roll number or lot"
          data-testid="qc-materials-search" className="rounded-sm h-8 text-sm max-w-xl" />
      </div>
      {!rolls && <div className="px-5 py-6 text-sm text-neutral-400">Loading…</div>}
      {rolls && groups.length === 0 && (
        <div className={`px-5 py-6 text-sm ${q ? "text-neutral-500" : "text-emerald-700"}`} data-testid="qc-materials-empty">
          {q ? "No roll waiting for QC matches that." : "Nothing waiting: every received roll has its QC."}
        </div>
      )}
      {groups.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="qc-materials-table">
            <thead className="bg-neutral-50 label-caps text-[10px]">
              <tr>
                <th className="w-8" />
                <th className="text-left px-3 py-2">Received</th>
                <th className="text-left px-3 py-2">Supplier · GRN</th>
                <th className="text-left px-3 py-2">Material</th>
                <th className="text-right px-3 py-2">Rolls</th>
                <th className="text-right px-3 py-2">Quantity</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const wait = daysSince(g.received);
                const sum = g.rolls.reduce((s, r) => s + (Number(r.balance) || 0), 0);
                const sel = chosen(g);
                return [
                  <tr key={g.key} className="border-t border-neutral-100" data-testid="qc-materials-row">
                    <td className="pl-3">
                      <button type="button" onClick={() => setOpen({ ...open, [g.key]: !open[g.key] })} className="text-neutral-400 hover:text-neutral-800" title="Show the rolls">
                        {open[g.key] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">
                      {g.received || "—"}
                      {wait != null && <div className={`text-[11px] ${wait > 2 ? "text-rose-600" : "text-neutral-500"}`}>{wait === 0 ? "today" : `${wait} day${wait === 1 ? "" : "s"} waiting`}</div>}
                    </td>
                    <td className="px-3 py-2 text-xs">{g.supplier || "—"}{g.grn && <div className="font-mono-num text-neutral-500">GRN {g.grn}</div>}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-sm">{g.item || g.code}</div>
                      <div className="text-[11px] text-neutral-500 font-mono-num">{g.code}{g.width ? ` · ${g.width} mm` : ""}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-mono-num">{g.rolls.length}</td>
                    <td className="px-3 py-2 text-right font-mono-num whitespace-nowrap">{qty(sum, 0)} {unit(g.uom)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" data-testid="qc-materials-record"
                        onClick={() => setQcFor(sel.length ? sel : g.rolls.map((r) => r.roll_no))}>
                        {sel.length ? `Record QC · ${sel.length} selected` : `Record QC · all ${g.rolls.length}`}
                      </Button>
                    </td>
                  </tr>,
                  open[g.key] && (
                    <tr key={`${g.key}-rolls`} className="bg-neutral-50/60">
                      <td />
                      <td colSpan={6} className="px-3 py-2">
                        <div className="flex flex-wrap gap-x-4 gap-y-1">
                          {g.rolls.map((r) => (
                            <label key={r.roll_no} className="inline-flex items-center gap-1.5 text-xs font-mono-num cursor-pointer">
                              <input type="checkbox" checked={!!picked[r.roll_no]} onChange={(e) => setPicked({ ...picked, [r.roll_no]: e.target.checked })} className="accent-[#FF5A00]" />
                              {r.roll_no} <span className="text-neutral-400">{qty(r.balance, 0)} {unit(r.uom)}</span>
                            </label>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
          {total > rolls.length && <div className="px-5 py-2 text-xs text-neutral-500">Oldest {rolls.length} of {total.toLocaleString("en-IN")} rolls shown.</div>}
        </div>
      )}
      {qcFor && <QcDialog rollNos={qcFor} onClose={() => setQcFor(null)} onDone={done} />}
    </section>
  );
}
